// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Pencil, Copy, Archive, Plus, Shield, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CONSTRAINT_TYPES = [
  { v: "team_blackout", label: "Blackout window" },
  { v: "must_play_before", label: "Latest game time" },
  { v: "must_play_after", label: "Earliest game time" },
  { v: "coach_conflict", label: "Same-coach conflict" },
  { v: "venue_preference", label: "Venue preference" },
  { v: "game_count_limit", label: "Game count limit" },
  { v: "divisional_ordering", label: "Avoid specific matchup" },
  { v: "do_not_play", label: "Do not play (specific team)" },
  { v: "must_play_same_time", label: "Must play same time as team" },

];

const SEVERITIES = [
  { v: "hard", label: "Hard rule", chip: "bg-red-100 text-red-700 border-red-200" },
  { v: "soft", label: "Soft preference", chip: "bg-amber-100 text-amber-700 border-amber-200" },
  { v: "preference", label: "Preference", chip: "bg-gray-100 text-gray-700 border-gray-200" },
];

const COACH_INPUT_OPTIONS = [
  "specific_time", "related_team_name", "related_team_age_group",
  "preferred_venue", "avoided_team_name", "flight_time",
  "games_friday", "games_saturday", "games_sunday",
];

const UNIVERSAL = "__universal__";

function sevChip(v: string) {
  const o = SEVERITIES.find((s) => s.v === v) ?? SEVERITIES[1];
  return <span className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded border ${o.chip}`}>{o.label}</span>;
}

interface Template {
  id: string;
  organization_id: string | null;
  sport_id: string | null;
  constraint_type: string;
  label: string;
  description: string | null;
  default_severity: string;
  requires_review: boolean;
  default_fee: number;
  default_parameters: Record<string, any>;
  coach_input_fields: string[];
  display_order: number;
  is_active: boolean;
}

const EMPTY_TEMPLATE: Partial<Template> = {
  sport_id: null,
  constraint_type: "team_blackout",
  label: "",
  description: "",
  default_severity: "soft",
  requires_review: true,
  default_fee: 0,
  default_parameters: {},
  coach_input_fields: [],
  is_active: true,
};

export default function ConstraintsCatalogPage() {
  const { organization } = useOrganization();
  const orgId = organization?.id;

  const [sports, setSports] = useState<{ id: string; name: string }[]>([]);
  const [filterSport, setFilterSport] = useState<string>(""); // "" = none yet
  const [showArchived, setShowArchived] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);

  const [editing, setEditing] = useState<Partial<Template> | null>(null);
  const [paramsText, setParamsText] = useState("{}");

  // Load sports
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("sports").select("id, name").order("name");
      setSports(data ?? []);
      // Default: Basketball
      const bb = (data ?? []).find((s: any) => s.name === "Basketball");
      setFilterSport(bb?.id ?? data?.[0]?.id ?? UNIVERSAL);
    })();
  }, []);

  const loadTemplates = async () => {
    if (!filterSport) return;
    setLoading(true);
    let q = (supabase as any).from("constraint_templates").select("*");
    if (filterSport === UNIVERSAL) {
      q = q.is("sport_id", null);
    } else {
      q = q.or(`sport_id.eq.${filterSport},sport_id.is.null`);
    }
    if (orgId) q = q.or(`organization_id.eq.${orgId},organization_id.is.null`);
    q = q.eq("is_active", !showArchived);
    const { data, error } = await q.order("display_order");
    if (error) {
      toast.error("Failed to load templates");
      setLoading(false);
      return;
    }
    setTemplates(data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSport, showArchived, orgId]);

  const openNew = () => {
    setEditing({
      ...EMPTY_TEMPLATE,
      sport_id: filterSport === UNIVERSAL ? null : filterSport,
    });
    setParamsText("{}");
  };

  const openEdit = (t: Template) => {
    setEditing({ ...t });
    setParamsText(JSON.stringify(t.default_parameters ?? {}, null, 2));
  };

  const duplicateTpl = async (t: Template) => {
    const { id, ...rest } = t;
    const { error } = await (supabase as any).from("constraint_templates").insert({
      ...rest,
      label: `${t.label} (copy)`,
      organization_id: orgId ?? null,
    });
    if (error) toast.error(error.message);
    else { toast.success("Duplicated"); loadTemplates(); }
  };

  const archiveTpl = async (t: Template) => {
    const { error } = await (supabase as any)
      .from("constraint_templates")
      .update({ is_active: !t.is_active })
      .eq("id", t.id);
    if (error) toast.error(error.message);
    else { toast.success(t.is_active ? "Archived" : "Restored"); loadTemplates(); }
  };

  const saveTemplate = async () => {
    if (!editing) return;
    if (!editing.label?.trim()) {
      toast.error("Label is required");
      return;
    }
    let parsed: any = {};
    try {
      parsed = paramsText.trim() ? JSON.parse(paramsText) : {};
    } catch {
      toast.error("Default parameters must be valid JSON");
      return;
    }
    const payload: any = {
      organization_id: editing.organization_id ?? orgId ?? null,
      sport_id: editing.sport_id ?? null,
      constraint_type: editing.constraint_type,
      label: editing.label.trim(),
      description: editing.description ?? null,
      default_severity: editing.default_severity,
      requires_review: !!editing.requires_review,
      default_fee: Number(editing.default_fee) || 0,
      default_parameters: parsed,
      coach_input_fields: editing.coach_input_fields ?? [],
      is_active: editing.is_active ?? true,
    };
    let error;
    if (editing.id) {
      ({ error } = await (supabase as any)
        .from("constraint_templates")
        .update(payload)
        .eq("id", editing.id));
    } else {
      ({ error } = await (supabase as any)
        .from("constraint_templates")
        .insert(payload));
    }
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Saved");
    setEditing(null);
    loadTemplates();
  };

  const sportName = (id: string | null) => sports.find((s) => s.id === id)?.name ?? "Universal";

  const visibleTemplates = useMemo(() => templates, [templates]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6" /> Constraint Templates
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Reusable scheduling constraints teams can request at registration. Filter by sport.
            Available across all events of that sport.
          </p>
        </div>
        <Button onClick={openNew}>
          <Plus className="w-4 h-4 mr-1" /> Add Template
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-4 mb-4">
        <select
          value={filterSport}
          onChange={(e) => setFilterSport(e.target.value)}
          className="border border-border rounded px-3 py-2 text-sm bg-background"
        >
          {sports.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
          <option value={UNIVERSAL}>Universal (all sports)</option>
        </select>

        <div className="flex gap-1 border border-border rounded overflow-hidden">
          <button
            onClick={() => setShowArchived(false)}
            className={`px-3 py-1.5 text-xs ${!showArchived ? "bg-primary text-primary-foreground" : "bg-background"}`}
          >Active</button>
          <button
            onClick={() => setShowArchived(true)}
            className={`px-3 py-1.5 text-xs ${showArchived ? "bg-primary text-primary-foreground" : "bg-background"}`}
          >Archived</button>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : visibleTemplates.length === 0 ? (
        <div className="p-12 border border-dashed border-border rounded-lg text-center">
          <Shield className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm font-medium">No templates yet for this filter.</p>
          <Button variant="link" onClick={openNew}>Add the first one</Button>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Label</th>
                <th className="text-left px-3 py-2">Sport</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Severity</th>
                <th className="text-left px-3 py-2">Fee</th>
                <th className="text-left px-3 py-2">Coach Inputs</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {visibleTemplates.map((t) => (
                <tr key={t.id} className="hover:bg-muted/30">
                  <td className="px-3 py-2">
                    <div className="font-medium">{t.label}</div>
                    {t.description && <div className="text-xs text-muted-foreground line-clamp-1">{t.description}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{sportName(t.sport_id)}</td>
                  <td className="px-3 py-2 text-xs">{CONSTRAINT_TYPES.find((c) => c.v === t.constraint_type)?.label ?? t.constraint_type}</td>
                  <td className="px-3 py-2">{sevChip(t.default_severity)}</td>
                  <td className="px-3 py-2 text-xs">{Number(t.default_fee) > 0 ? `$${Number(t.default_fee).toFixed(2)}` : "Free"}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {t.coach_input_fields?.length ? `${t.coach_input_fields.length} field${t.coach_input_fields.length === 1 ? "" : "s"}` : "Pre-set"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => openEdit(t)} className="text-muted-foreground hover:text-foreground p-1" title="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => duplicateTpl(t)} className="text-muted-foreground hover:text-foreground p-1" title="Duplicate">
                      <Copy className="w-4 h-4" />
                    </button>
                    <button onClick={() => archiveTpl(t)} className="text-muted-foreground hover:text-foreground p-1" title={t.is_active ? "Archive" : "Restore"}>
                      <Archive className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit Template" : "New Constraint Template"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium">Sport</label>
                  <select
                    value={editing.sport_id ?? UNIVERSAL}
                    onChange={(e) => setEditing({ ...editing, sport_id: e.target.value === UNIVERSAL ? null : e.target.value })}
                    className="w-full border border-border rounded px-2 py-1.5 text-sm bg-background"
                  >
                    {sports.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    <option value={UNIVERSAL}>Universal (all sports)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium">Constraint Type</label>
                  <select
                    value={editing.constraint_type}
                    onChange={(e) => setEditing({ ...editing, constraint_type: e.target.value })}
                    className="w-full border border-border rounded px-2 py-1.5 text-sm bg-background"
                  >
                    {CONSTRAINT_TYPES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium">Label *</label>
                <Input
                  value={editing.label ?? ""}
                  onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                  placeholder="e.g. No Friday games"
                />
              </div>

              <div>
                <label className="text-xs font-medium">Description</label>
                <Textarea
                  value={editing.description ?? ""}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  rows={2}
                  placeholder="What teams will see when picking this constraint"
                />
              </div>

              <div>
                <label className="text-xs font-medium">Default Severity</label>
                <div className="flex gap-2 mt-1">
                  {SEVERITIES.map((s) => (
                    <label key={s.v} className={`flex items-center gap-1 px-2 py-1 border rounded cursor-pointer text-xs ${editing.default_severity === s.v ? "border-primary bg-primary/5" : "border-border"}`}>
                      <input
                        type="radio"
                        name="severity"
                        checked={editing.default_severity === s.v}
                        onChange={() => setEditing({ ...editing, default_severity: s.v })}
                      />
                      {s.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium">Default Fee ($)</label>
                  <Input
                    type="number"
                    value={editing.default_fee ?? 0}
                    onChange={(e) => setEditing({ ...editing, default_fee: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <label className="flex items-center gap-2 text-sm mt-5">
                  <input
                    type="checkbox"
                    checked={!!editing.requires_review}
                    onChange={(e) => setEditing({ ...editing, requires_review: e.target.checked })}
                  />
                  Requires review
                </label>
              </div>

              <div>
                <label className="text-xs font-medium">Coach Input Fields</label>
                <p className="text-[11px] text-muted-foreground mb-1">
                  What does the coach need to provide when picking this template? Leave empty for fully pre-set.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {COACH_INPUT_OPTIONS.map((f) => {
                    const on = editing.coach_input_fields?.includes(f);
                    return (
                      <button
                        key={f}
                        type="button"
                        onClick={() => {
                          const cur = editing.coach_input_fields ?? [];
                          setEditing({
                            ...editing,
                            coach_input_fields: on ? cur.filter((x) => x !== f) : [...cur, f],
                          });
                        }}
                        className={`text-xs px-2 py-1 rounded border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border"}`}
                      >
                        {f}
                      </button>
                    );
                  })}
                </div>
              </div>

              <details>
                <summary className="text-xs font-medium cursor-pointer">Advanced — Default Parameters (JSON)</summary>
                <Textarea
                  value={paramsText}
                  onChange={(e) => setParamsText(e.target.value)}
                  rows={5}
                  className="font-mono text-xs mt-2"
                  placeholder='{ "blackout_days": ["fri"] }'
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Examples: <code>{`{"blackout_days":["fri"]}`}</code>,{" "}
                  <code>{`{"day":"sun","must_finish_by":"12:00"}`}</code>,{" "}
                  <code>{`{"prefer_same_venue":true}`}</code>
                </p>
              </details>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editing.is_active ?? true}
                  onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
                />
                Active
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveTemplate}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
